import { downloadCSV } from "ra-core";
import { logError } from "../../../utils/appUtils";
import {
  FilePaths,
  InboundStatus,
  Orientations,
} from "../../../utils/constants";
import { buildHeader, fillDownRows } from "../../../utils/exportsUtils";
import {
  generateAndDownloadPdf,
  generateFileName,
} from "../../../utils/fileUtils";
import jsonExport from "jsonexport/dist";
import {
  formatDate,
  getFormattedStartAndEndDate,
} from "../../../utils/dateUtils";
import { exportCustomField, formatCurrency } from "../../../utils/utils";
import { getTotalInvoiced } from "../invoices/InvoiceUtils";

export const inboundExportPdf = async (
  data,
  inputStartDate,
  inputEndDate,
  notify,
  filter,
) => {
  try {
    const reloadYardId = window.appUser?.reloadYardId;

    const { formattedStartDate, formattedEndDate } =
      getFormattedStartAndEndDate(
        data,
        inputStartDate,
        inputEndDate,
        "endDateTime",
      );

    const items = data.map(
      ({
        accountName = "--",
        poNumber = "--",
        railCarNumber = "--",
        id = "--",
        endDateTime,
        invoice,
        invoicedServices,
      }) => {
        const invoiceNumber = invoice?.invoiceNumber || "--";
        const computedTotal = getTotalInvoiced({ invoicedServices });

        return {
          accountName,
          orderNumber: poNumber,
          invoiceNumber: invoiceNumber || "--",
          carNumber: railCarNumber,
          inbound: id,
          inboundDate: endDateTime ? formatDate(new Date(endDateTime)) : "--",
          amountBilled: computedTotal ? formatCurrency(computedTotal) : "--",
          total: computedTotal || 0,
        };
      },
    );

    // Calculate display flags
    const showAmounts =
      filter?.inboundShipmentByReloadYardByStatusByDates
        ?.statusReleaseDateTimePlacedDateTimeNotifyDateTimeScheduledDateTimeCreateDateTime
        ?.beginsWith?.status === InboundStatus.RELEASED ||
      filter?.searchInboundShipments?.status?.eq === InboundStatus.RELEASED;

    const showReceivedColumn = !items.every(
      ({ inboundDate }) => inboundDate === "--",
    );

    // Calculate totals
    const totalAmountBilled = items.reduce(
      (sum, { total }) => sum + (total || 0),
      0,
    );

    // Generate file names
    const fileName = generateFileName({ resource: "inbound", ext: "pdf" });
    const downloadName = generateFileName({
      resource: "inbound",
      ext: "pdf",
      includeDate: true,
    });

    // Build columns array
    const columns = [
      { label: "Order #", source: "orderNumber" },
      { label: "Car Number", source: "carNumber" },
    ];

    if (showReceivedColumn) {
      columns.push({ label: "Received", source: "inboundDate" });
    }

    if (showAmounts) {
      columns.push(
        { label: "Invoice #", source: "invoiceNumber" },
        { label: "Invoiced", source: "amountBilled" },
      );
    }

    if (!filter?.accountId?.eq) {
      columns.unshift({ label: "Customer", source: "accountName" });
    }

    // Build header
    const header = buildHeader(
      data,
      formattedStartDate,
      formattedEndDate,
      filter,
    );

    // Build footer
    const footer = [
      {
        label: "Total Loads",
        value: data.length,
      },
    ];

    if (showAmounts) {
      footer.unshift({
        label: "Total Invoiced",
        value: formatCurrency(totalAmountBilled) || "",
      });
    }

    const templateKey = `${FilePaths.TEMPLATES_PDF}/${FilePaths.TEMPLATE_REPORT}`;
    const outputFileKey = `${reloadYardId}/${reloadYardId}/${FilePaths.INVENTORY}/${fileName}`;

    await generateAndDownloadPdf({
      templateKey,
      outputFileKey,
      downloadName,
      data: {
        metaData: {
          orientation: Orientations.PORTRAIT,
          currentDate: new Date().toLocaleString(),
        },
        content: {
          header,
          title: "INBOUND LOAD REPORT",
          table: {
            columns,
            items,
          },
          footer,
        },
      },
    });
  } catch (err) {
    logError("inbound:exportPdf", err, notify);
  }
};

export const inboundExportCsv = (inbounds) => {
  const customHeaderMap = new Map();
  debugger;

  const inboundsForExport = inbounds.map((inbound) => {
    const inboundForExport = { ...inbound };

    if (inbound.inventories?.items) {
      inboundForExport.tallySheet = inbound.inventories.items.map(
        (inventory) => {
          const lineItem = {
            inventoryId: inventory.id,
            productCode: inventory.productCode,
            productName: inventory.productName,
            lotNumber: inbound.lotNumber,
            dateReceived: inbound.placedDateTime
              ? new Date(inbound.placedDateTime).toLocaleDateString()
              : "",
            quantityReceived: inventory.quantityReceived,
            UOM: inventory.inventoryUOM,
            pieces: inventory.pcs,
          };

          return lineItem;
        },
      );
    }
    delete inboundForExport.inventories;

    if (inbound.customFields?.length > 0) {
      debugger;
      inbound.customFields.forEach((field) => {
        exportCustomField(field, inboundForExport, customHeaderMap);
      });
    }

    delete inboundForExport.account;
    delete inboundForExport.accountId;
    delete inboundForExport.area;
    delete inboundForExport.areaId;
    delete inboundForExport.assignedTo;
    delete inboundForExport.carrierType;
    delete inboundForExport.createdAt;
    delete inboundForExport.createDateTime;
    delete inboundForExport.daysOnDemurrageClock;
    delete inboundForExport.endDateTime;
    delete inboundForExport.groups;
    delete inboundForExport.history;
    delete inboundForExport.invoiceId;
    delete inboundForExport.notes;
    delete inboundForExport.owner;
    delete inboundForExport.attachments;
    delete inboundForExport.photos;
    delete inboundForExport.reloadYardCommodity;
    delete inboundForExport.reloadYardCommodityId;
    delete inboundForExport.reloadYardId;
    delete inboundForExport.seals;
    delete inboundForExport.startDateTime;
    delete inboundForExport.state;
    delete inboundForExport.stripeBillingDateTime;
    delete inboundForExport.updatedAt;
    delete inboundForExport.verificationRequired;
    delete inboundForExport.zone;
    delete inboundForExport.zoneId;
    debugger;

    return inboundForExport;
  });

  jsonExport(
    fillDownRows(inboundsForExport),
    { rename: ["inboundShipmentId"] },
    (err, csv) => {
      downloadCSV(csv, `inboundShipments_${new Date().toLocaleDateString()}`); // download as 'inboundShipments.csv` file
    },
  );
};
